import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ApplicationService from 'src/services/ApplicationService';
import { enterFullscreen, exitFullscreen, isFullscreen } from 'src/utils/browserUtils';
import {
  SELECTOR_UI_FULLSCREEN,
  SELECTOR_UI_SETTINGS_VISIBLE,
  UIActionTypes,
  SELECTOR_UI_IS_PICTURE_IN_PICTURE_ENABLED,
  SELECTOR_UI_IS_USER_ACTIVE,
  SELECTOR_UI_SUBTITLE_SETTINGS_VISIBLE,
} from 'src/store/reducers/ui';

const useUI = () => {
  const fullscreen = useSelector(SELECTOR_UI_FULLSCREEN);
  const settingsVisible = useSelector(SELECTOR_UI_SETTINGS_VISIBLE);
  const isPictureInPictureEnabled = useSelector(SELECTOR_UI_IS_PICTURE_IN_PICTURE_ENABLED);
  const isUserActive = useSelector(SELECTOR_UI_IS_USER_ACTIVE);
  const subtitleSettingsVisible = useSelector(SELECTOR_UI_SUBTITLE_SETTINGS_VISIBLE);

  const dispatch = useDispatch();

  const toggleFullscreen = useCallback(() => {
    isFullscreen() ? exitFullscreen() : enterFullscreen();
  }, []);

  const toggleUserActive = useCallback(() => {
    ApplicationService.player?.setUserActive(!ApplicationService.player?.userActive);
  }, []);

  const setUserActive = useCallback((active: boolean) => {
    ApplicationService.player?.setUserActive(active);
  }, []);

  const showSettings = useCallback(() => {
    dispatch({
      type: UIActionTypes.SHOW_SETTINGS,
    });
  }, []);

  const hideSettings = useCallback(() => {
    dispatch({
      type: UIActionTypes.HIDE_SETTINGS,
    });
  }, [dispatch]);

  const toggleSettings = useCallback(() => {
    return settingsVisible ? hideSettings() : showSettings();
  }, [hideSettings, settingsVisible, showSettings]);

  const togglePictureInPicture = useCallback(() => {
    ApplicationService.player?.togglePictureInPicture();
  }, []);

  const showSubtitleSettings = useCallback(() => {
    dispatch({
      type: UIActionTypes.SHOW_SUBTITLE_SETTINGS,
    });
  }, [dispatch]);

  const hideSubtitleSettings = useCallback(() => {
    dispatch({
      type: UIActionTypes.HIDE_SUBTITLE_SETTINGS,
    });
  }, [dispatch]);

  const toggleSubtitleSettings = useCallback(() => {
    return subtitleSettingsVisible ? hideSubtitleSettings() : showSubtitleSettings();
  }, [subtitleSettingsVisible, showSubtitleSettings, hideSubtitleSettings]);

  return {
    fullscreen,
    toggleFullscreen,
    toggleUserActive,
    showSettings,
    hideSettings,
    settingsVisible,
    isPictureInPictureEnabled,
    isUserActive,
    setUserActive,
    toggleSettings,
    togglePictureInPicture,
    subtitleSettingsVisible,
    showSubtitleSettings,
    hideSubtitleSettings,
    toggleSubtitleSettings,
  };
};

export default useUI;
